import React, { useState, useRef, useCallback, useEffect } from 'react'
import { AnimatePresence } from 'framer-motion'
import { toast } from 'sonner'

import { NotificationButton } from '../NotificationButton'
import { NotificationDropdown } from '../NotificationDropdown'

import * as S from './NotificationAppHeader.styles'
import type { NotificationAppHeaderProps } from './NotificationAppHeader.types'

import type { EnhancedNotificationItem } from '@/features/notifications'
import { useNotifications } from '@/features/notifications'
import { notificationConfig } from '@/features/notifications'
import { useAppNavigation } from '@/hooks/useAppNavigation'
import { processNotificationUrl, getNavigationErrorMessage } from '@/utils/notificationNavigation'
import { resetFaviconBadge, setFaviconBadgeCount, pulseFaviconBadge } from '@/utils/faviconBadge'
import { setTabTitleCount, resetTabTitleCount } from '@/utils/tabTitleBadge'
import { useNotificationSound } from '@/features/notifications/hooks/useNotificationSound'

/**
 * Main notification component for the app header
 *
 * This component integrates the notification button and dropdown
 * into a cohesive notification experience. It manages all state and coordinates
 * between the different components.
 */
export function NotificationAppHeader({ initialUserType = 'manager', className }: NotificationAppHeaderProps) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [announcement, setAnnouncement] = useState<string>('')
    const [lastOpenedAt, setLastOpenedAt] = useState<number | null>(null)

    const containerRef = useRef<HTMLDivElement>(null)
    const bellRef = useRef<{ triggerGiggle?: () => void; triggerBadgeBounce?: () => void; triggerRipple?: () => void }>(
        null,
    )
    const lastSeenUnreadRef = useRef<number>(0)

    // Notification sound hook - single instance for entire app
    const { playNotification } = useNotificationSound()

    // Notification state management
    const {
        state: notificationState,
        actions: notificationActions,
        unreadCount,
    } = useNotifications(initialUserType, playNotification)

    // Modern navigation system
    const { navigateToPath } = useAppNavigation()

    // Handle dropdown toggle - refresh data when opening
    const handleDropdownToggle = useCallback(async () => {
        const wasOpen = isDropdownOpen
        setIsDropdownOpen((prev) => !prev)

        // Always refresh notification list when opening dropdown to ensure fresh data
        // This is the only time we actually need the notification content
        if (!wasOpen) {
            setLastOpenedAt(Date.now())
            await notificationActions.refreshNotificationsList()
        }
    }, [isDropdownOpen, notificationActions])

    const handleDropdownClose = useCallback(() => {
        setIsDropdownOpen(false)
    }, [])

    // Handle notification interactions with modern routing
    const handleNotificationClick = useCallback(
        (notification: EnhancedNotificationItem) => {
            // Close dropdown immediately for better UX
            setIsDropdownOpen(false)

            // Process the notification URL using modern routing system
            if (notification.internal_url) {
                const result = processNotificationUrl(notification.internal_url)

                switch (result.type) {
                    case 'internal':
                        navigateToPath(result.route)
                        break

                    case 'external':
                        window.open(result.route, '_blank', 'noopener,noreferrer')
                        break

                    case 'invalid':
                        toast.error(getNavigationErrorMessage(result.error))

                        break
                }
            }
        },
        [navigateToPath],
    )

    const handleNotificationDismiss = useCallback(
        (notificationId: string) => {
            notificationActions.dismissNotification(notificationId)

            // Announce to screen readers
            setAnnouncement('Notification dismissed')
            setTimeout(() => setAnnouncement(''), 1000)
        },
        [notificationActions],
    )

    const handleClearAll = useCallback(() => {
        notificationActions.clearAllNotifications()
        setIsDropdownOpen(false)

        // Announce to screen readers
        setAnnouncement('All notifications cleared')
        setTimeout(() => setAnnouncement(''), 1000)
    }, [notificationActions])

    const handleAcknowledgeNew = useCallback(() => {
        setLastOpenedAt(Date.now())
    }, [])

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: Event) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false)
            }
        }

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside)
            document.addEventListener('touchstart', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [isDropdownOpen])

    // Handle escape key to close dropdown
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isDropdownOpen) {
                setIsDropdownOpen(false)
            }
        }

        if (isDropdownOpen) {
            document.addEventListener('keydown', handleEscapeKey)
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey)
        }
    }, [isDropdownOpen])

    // Announce new notifications to screen readers
    useEffect(() => {
        if (notificationState.hasNewNotification && notificationConfig.accessibility.announceNewNotifications) {
            const count = unreadCount
            const message = count === 1 ? 'You have 1 new notification' : `You have ${count} new notifications`

            setAnnouncement(message)
            setTimeout(() => setAnnouncement(''), 3000)
        }
    }, [notificationState.hasNewNotification, unreadCount])

    // Title badge always reflects actual unread count; favicon dot is "new since hidden"
    useEffect(() => {
        setTabTitleCount(unreadCount)

        if (document.visibilityState === 'hidden') {
            if (unreadCount > lastSeenUnreadRef.current) {
                void setFaviconBadgeCount(unreadCount) // show dot while hidden only when count increased
            }
        } else {
            // Visible: dot should disappear
            resetFaviconBadge()
            lastSeenUnreadRef.current = unreadCount
        }

        return () => {
            // Safety on unmount
            resetFaviconBadge()
            resetTabTitleCount()
        }
    }, [unreadCount])

    // Pulse favicon on return to tab if new unread arrived while away
    useEffect(() => {
        const lastUnreadAtHideRef = { current: 0 }
        const handleVisibility = () => {
            if (document.visibilityState === 'hidden') {
                lastUnreadAtHideRef.current = unreadCount
                return
            }
            // Tab visible
            // Always show a soft ripple on return
            bellRef.current?.triggerRipple?.()

            // Pulse/bounce only if there were new unreads while away
            if (unreadCount > lastUnreadAtHideRef.current) {
                void pulseFaviconBadge(unreadCount)
                bellRef.current?.triggerBadgeBounce?.()
                // After the pulse, clear the dot (dot is only for hidden state)
                setTimeout(() => {
                    resetFaviconBadge()
                }, 280)
            } else {
                // Clear dot on focus (no new since hide)
                resetFaviconBadge()
            }
            lastSeenUnreadRef.current = unreadCount
        }

        document.addEventListener('visibilitychange', handleVisibility)
        window.addEventListener('focus', handleVisibility)
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility)
            window.removeEventListener('focus', handleVisibility)
        }
    }, [unreadCount])

    return (
        <S.NotificationAppHeaderContainer className={className}>
            <S.ClickOutsideHandler ref={containerRef}>
                <NotificationButton
                    ref={bellRef}
                    notifications={notificationState.notifications}
                    userType={notificationState.userType}
                    isOpen={isDropdownOpen}
                    isLoading={notificationState.isLoading}
                    unreadCount={unreadCount}
                    onToggle={handleDropdownToggle}
                />

                <S.DropdownContainer>
                    <AnimatePresence>
                        {isDropdownOpen && (
                            <NotificationDropdown
                                notifications={notificationState.notifications}
                                userType={notificationState.userType}
                                isOpen={isDropdownOpen}
                                isLoading={notificationState.isLoading}
                                isDismissingAll={notificationState.isDismissingAll}
                                totalNotifications={notificationState.totalNotifications}
                                hasMoreNotifications={notificationState.hasMoreNotifications}
                                isLoadingMore={notificationState.isLoadingMore}
                                onLoadMore={notificationActions.loadMoreNotifications}
                                onClose={handleDropdownClose}
                                onNotificationClick={handleNotificationClick}
                                onNotificationDismiss={handleNotificationDismiss}
                                onClearAll={handleClearAll}
                                lastOpenedAt={lastOpenedAt}
                                onAcknowledgeNew={handleAcknowledgeNew}
                            />
                        )}
                    </AnimatePresence>
                </S.DropdownContainer>
            </S.ClickOutsideHandler>

            {/* Screen Reader Announcements */}
            <S.AnnouncementContainer role="status" aria-live="polite">
                {announcement}
            </S.AnnouncementContainer>
        </S.NotificationAppHeaderContainer>
    )
}

// Export component with display name for debugging
NotificationAppHeader.displayName = 'NotificationAppHeader'
